/**
 * Size guide CM/IN toggle.
 * Page images (order):
 *   2 → CM, IN
 *   4 → Desktop CM, Desktop IN, Mobile CM, Mobile IN
 * Unisex size-chart page ([data-cw-size-guide-gender]), 8 images:
 *   first 4 Men's, next 4 Women's, same order. Toggled via Men/Women and CM/IN.
 *
 * For sharp mobile retina, export mobile charts ≥1500px wide
 * (750px masters look soft on 2x/3x phones).
 */
const CW_SG_MOBILE_MQ = '(max-width: 749px)';
const CW_SG_MOBILE_WIDTHS = [375, 750, 1125, 1500, 2250];
const CW_SG_DESKTOP_WIDTHS = [540, 1081, 1622, 2162];
const CW_SG_DESKTOP_SIZES = '(min-width: 1920px) 54rem, (min-width: 1440px) 48rem, 42rem';
const CW_SG_MOBILE_SIZES = '100vw';

function updateSizeGuidePanels(root) {
  const currentUnit = root.getAttribute('data-cw-active-unit') || root.dataset.cwActiveUnit || 'cm';
  const currentGender = root.getAttribute('data-cw-active-gender') || root.dataset.cwActiveGender || 'men';
  const hasGender = root.hasAttribute('data-cw-size-guide-gender');

  root.querySelectorAll('[data-cw-unit-panel]').forEach((panel) => {
    const matchUnit = panel.getAttribute('data-cw-unit-panel') === currentUnit;
    const matchGender = !hasGender || panel.getAttribute('data-cw-gender-panel') === currentGender;
    panel.hidden = !(matchUnit && matchGender);
  });
}

function setSizeGuideUnit(root, unit) {
  if (!root || (unit !== 'cm' && unit !== 'in')) return;
  root.setAttribute('data-cw-active-unit', unit);
  root.dataset.cwActiveUnit = unit;

  root.querySelectorAll('button[data-cw-unit]').forEach((btn) => {
    const active = btn.getAttribute('data-cw-unit') === unit;
    btn.classList.toggle('is-active', active);
    btn.setAttribute('aria-pressed', String(active));
  });

  updateSizeGuidePanels(root);
}

function setSizeGuideGender(root, gender) {
  if (!root || (gender !== 'men' && gender !== 'women')) return;
  root.setAttribute('data-cw-active-gender', gender);
  root.dataset.cwActiveGender = gender;

  root.querySelectorAll('button[data-cw-gender]').forEach((btn) => {
    const active = btn.getAttribute('data-cw-gender') === gender;
    btn.classList.toggle('is-active', active);
    btn.setAttribute('aria-pressed', String(active));
  });

  updateSizeGuidePanels(root);
}

/** Full-size URL: linked original > largest srcset > src. */
function rawImgSrc(img) {
  const link = img.closest('a[href]');
  if (link) {
    const href = (link.getAttribute('href') || '').trim();
    if (href && /\.(jpe?g|png|webp|gif)(\?|$)/i.test(href)) return href;
  }

  const srcset = img.getAttribute('srcset');
  if (srcset) {
    let best = '';
    let bestW = -1;
    srcset.split(',').forEach((part) => {
      const bits = part.trim().split(/\s+/);
      const url = bits[0];
      if (!url) return;
      const desc = bits[1] || '';
      const w = desc.endsWith('w') ? parseInt(desc, 10) : 0;
      if (w >= bestW) {
        best = url;
        bestW = w;
      }
    });
    if (best) return best;
  }

  return (img.getAttribute('src') || img.currentSrc || '').trim();
}

/** Original file URL — strip size suffix / width (never ask CDN to upscale). */
function masterUrl(url) {
  if (!url) return url;
  try {
    const u = new URL(url, window.location.origin);
    u.pathname = u.pathname.replace(/_(?:\d+x\d+|\d+x)(?=\.[^./]+$)/, '');
    ['width', 'height', 'crop', 'pad_color'].forEach((k) => u.searchParams.delete(k));
    return u.href;
  } catch {
    return url;
  }
}

function shopifyImageUrl(url, width) {
  if (!url) return url;
  try {
    const u = new URL(masterUrl(url), window.location.origin);
    if (width > 0) u.searchParams.set('width', String(width));
    return u.href;
  } catch {
    return url;
  }
}

function srcsetFor(url, widths, maxWidth) {
  const list = widths.filter((w) => w < maxWidth);
  list.push(maxWidth);
  return [...new Set(list)]
    .sort((a, b) => a - b)
    .map((w) => {
      const src = w >= maxWidth ? masterUrl(url) : shopifyImageUrl(url, w);
      return `${src} ${w}w`;
    })
    .join(', ');
}

/**
 * @param {HTMLImageElement | HTMLSourceElement} target
 */
function bindSrcset(target, master, widths, sizes, fallbackMax) {
  const apply = (maxWidth) => {
    target.srcset = srcsetFor(master, widths, maxWidth);
    target.sizes = sizes;
    if (target instanceof HTMLImageElement) {
      target.src = masterUrl(master);
    }
  };

  apply(fallbackMax);

  const probe = new Image();
  probe.onload = () => apply(probe.naturalWidth || fallbackMax);
  probe.onerror = () => apply(fallbackMax);
  probe.src = masterUrl(master);
}

function createChartMedia(desktopImg, mobileImg, eager) {
  const desktopMaster = masterUrl(rawImgSrc(desktopImg));
  const alt = desktopImg.getAttribute('alt') || '';

  const img = document.createElement('img');
  img.className = 'cw-size-guide__image';
  img.alt = alt;
  img.decoding = 'async';
  img.loading = eager ? 'eager' : 'lazy';
  if (eager) img.fetchPriority = 'high';

  const mobileSrc = mobileImg ? rawImgSrc(mobileImg) : '';
  const mobileMaster = mobileSrc ? masterUrl(mobileSrc) : '';

  if (mobileMaster) {
    const picture = document.createElement('picture');
    const source = document.createElement('source');
    source.media = CW_SG_MOBILE_MQ;
    bindSrcset(source, mobileMaster, CW_SG_MOBILE_WIDTHS, CW_SG_MOBILE_SIZES, 750);
    picture.appendChild(source);

    bindSrcset(img, desktopMaster, CW_SG_DESKTOP_WIDTHS, CW_SG_DESKTOP_SIZES, 1081);
    picture.appendChild(img);
    return picture;
  }

  bindSrcset(
    img,
    desktopMaster,
    CW_SG_DESKTOP_WIDTHS,
    `(max-width: 749px) 100vw, ${CW_SG_DESKTOP_SIZES}`,
    1081
  );
  return img;
}

function buildUnitPanel(unit, gender) {
  const panel = document.createElement('div');
  panel.className = 'cw-size-guide__panel';
  panel.setAttribute('data-cw-unit-panel', unit);
  if (gender) panel.setAttribute('data-cw-gender-panel', gender);
  return panel;
}

function buildUnitMedia(unit, desktopImg, mobileImg, gender) {
  const panel = buildUnitPanel(unit, gender);
  const isEager = unit === 'cm' && (!gender || gender === 'men');
  panel.appendChild(createChartMedia(desktopImg, mobileImg, isEager));
  return panel;
}

/**
 * Init (or re-init) from page HTML.
 * Theme morph / view-transitions can restore raw source while leaving
 * data-cw-size-guide-ready="true" — that skips transform and shows all charts.
 * Always re-process when [data-cw-size-guide-source] is present.
 */
function initSizeGuideFromPage(root) {
  if (!root || root.dataset.cwSizeGuideBusy === 'true') return;

  const sourceEl = root.querySelector('[data-cw-size-guide-source]');
  if (!sourceEl) {
    if (root.querySelector('[data-cw-unit-panel]')) {
      const defaultUnit = root.dataset.cwUnit || root.getAttribute('data-cw-default-unit') || 'cm';
      if (root.hasAttribute('data-cw-size-guide-gender')) {
        const defaultGender = root.dataset.cwGender || root.getAttribute('data-cw-default-gender') || 'men';
        setSizeGuideGender(root, defaultGender);
      }
      setSizeGuideUnit(root, defaultUnit);
      root.dataset.cwSizeGuideReady = 'true';
    }
    return;
  }

  root.dataset.cwSizeGuideBusy = 'true';
  delete root.dataset.cwSizeGuideReady;
  root.querySelectorAll('[data-cw-unit-panel]').forEach((panel) => panel.remove());

  const images = Array.from(sourceEl.querySelectorAll('img')).filter((img) => rawImgSrc(img));

  if (images.length < 2) {
    const toggles = root.querySelectorAll('.cw-size-guide__toggle');
    toggles.forEach((t) => (t.hidden = true));
    delete root.dataset.cwSizeGuideBusy;
    root.dataset.cwSizeGuideReady = 'true';
    return;
  }

  const hasGender = root.hasAttribute('data-cw-size-guide-gender') && images.length >= 4;
  const panels = [];

  if (hasGender) {
    if (images.length >= 8) {
      // 0: Men Desktop CM, 1: Men Desktop IN, 2: Men Mobile CM, 3: Men Mobile IN
      // 4: Women Desktop CM, 5: Women Desktop IN, 6: Women Mobile CM, 7: Women Mobile IN
      panels.push(buildUnitMedia('cm', images[0], images[2], 'men'));
      panels.push(buildUnitMedia('in', images[1], images[3], 'men'));
      panels.push(buildUnitMedia('cm', images[4], images[6], 'women'));
      panels.push(buildUnitMedia('in', images[5], images[7], 'women'));
    } else {
      // 4 images (Men CM, Men IN, Women CM, Women IN)
      panels.push(buildUnitMedia('cm', images[0], null, 'men'));
      panels.push(buildUnitMedia('in', images[1], null, 'men'));
      panels.push(buildUnitMedia('cm', images[2], null, 'women'));
      panels.push(buildUnitMedia('in', images[3], null, 'women'));
    }
  } else {
    const genderToggle = root.querySelector('.cw-size-guide__toggle--gender');
    if (genderToggle) genderToggle.hidden = true;

    const hasMobile = images.length >= 4;
    panels.push(buildUnitMedia('cm', images[0], hasMobile ? images[2] : null));
    panels.push(buildUnitMedia('in', images[1], hasMobile ? images[3] : null));
  }

  sourceEl.replaceWith(...panels);

  if (hasGender) {
    const defaultGender = root.getAttribute('data-cw-active-gender') || root.getAttribute('data-cw-default-gender') || 'men';
    setSizeGuideGender(root, defaultGender);
  }
  const defaultUnit = root.getAttribute('data-cw-active-unit') || root.getAttribute('data-cw-default-unit') || 'cm';
  setSizeGuideUnit(root, defaultUnit);

  delete root.dataset.cwSizeGuideBusy;
  root.dataset.cwSizeGuideReady = 'true';
}

function initSizeGuides(scope) {
  (scope || document).querySelectorAll('[data-cw-size-guide-page]').forEach(initSizeGuideFromPage);
}

window.cwInitSizeGuides = initSizeGuides;
window.cwSetSizeGuideUnit = setSizeGuideUnit;
window.cwSetSizeGuideGender = setSizeGuideGender;

document.addEventListener('click', (event) => {
  const unitBtn = event.target.closest('button[data-cw-unit]');
  if (unitBtn) {
    const root = unitBtn.closest('[data-cw-size-guide]');
    if (root) setSizeGuideUnit(root, unitBtn.getAttribute('data-cw-unit'));
    return;
  }

  const genderBtn = event.target.closest('button[data-cw-gender]');
  if (genderBtn) {
    const root = genderBtn.closest('[data-cw-size-guide]');
    if (root) setSizeGuideGender(root, genderBtn.getAttribute('data-cw-gender'));
  }
});

const boot = () => initSizeGuides(document);
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', boot);
} else {
  boot();
}

document.addEventListener(
  'toggle',
  (event) => {
    if (event.target instanceof HTMLDialogElement && event.target.open) {
      initSizeGuides(event.target);
    }
  },
  true
);

document.addEventListener('shopify:section:load', (event) => {
  initSizeGuides(event.target instanceof Element ? event.target : document);
});

window.addEventListener('pageshow', () => initSizeGuides(document));